import track, { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import { Button } from '@simplywallst/ui-core';

import useUser from '@/hooks/useUser';

import If from '@/components/If';

import type { SubNavRoute } from '@/constants/routes';

import { DesktopSidebarWrapper, List, ListItem } from './styled';

interface Props {
  fixed?: boolean;
  routes: SubNavRoute[];
  selected?: string | number;
  listType?: keyof JSX.IntrinsicElements;
  handleClick?: () => void;
}

const defaultProps = {
  fixed: true,
  listType: 'ul' as keyof JSX.IntrinsicElements,
  payload: {},
};

const DesktopSidebar = ({
  fixed,
  routes,
  selected,
  listType,
  handleClick,
}: Props) => {
  const { trackEvent } = useTracking();
  const { data: userData } = useUser();
  const shouldSeeSubscriptionAndBilling =
    RUNTIME_ENV !== 'native' &&
    userData.billing.status !== 'trialing' &&
    userData.billing.provider === 'stripe';
  return (
    <DesktopSidebarWrapper fixed={fixed}>
      <List as={listType as any} listType={listType as any}>
        {routes &&
          routes
            .filter((r) => r.label !== 'Viewed Companies')
            .map(
              (
                { to, href, anchor, onClick, id, label, payload, tracking },
                i
              ) => (
                <ListItem
                  key={`${to || href || anchor}-${i}`}
                  selected={selected === (to || href || anchor || id)}
                >
                  <If
                    conditions={{
                      functionHandler: typeof onClick !== 'undefined',
                      noSubscription:
                        label === 'Subscription & Billing' &&
                        !shouldSeeSubscriptionAndBilling,
                      toHandler: typeof to !== 'undefined',
                      hrefHandler: typeof href !== 'undefined',
                      anchorHandler: typeof anchor !== 'undefined',
                    }}
                  >
                    {{
                      functionHandler: (
                        <Button
                          data-cy-id={`desktop-sidebar-nav-${i}`}
                          styleType="softLink"
                          onClick={() => {
                            handleClick && handleClick();
                            trackEvent({
                              modifier:
                                tracking ||
                                (typeof label === 'string' ? label : undefined),
                            });
                            if (typeof onClick === 'function') {
                              onClick(id);
                            }
                          }}
                        >
                          {label}
                        </Button>
                      ),
                      noSubscription: null,
                      toHandler: (
                        <RouterLink
                          data-cy-id={`desktop-sidebar-nav-${i}`}
                          inheritColor
                          noUnderline
                          to={{ type: to as string, payload }}
                          onClick={() => {
                            handleClick && handleClick();
                            trackEvent({
                              modifier:
                                tracking ||
                                (typeof label === 'string' ? label : undefined),
                            });
                          }}
                        >
                          {label}
                        </RouterLink>
                      ),
                      hrefHandler: (
                        <Button
                          data-cy-id={`desktop-sidebar-nav-${i}`}
                          as="a"
                          styleType="softLink"
                          href={href}
                          onClick={() => {
                            handleClick && handleClick();
                            trackEvent({
                              modifier:
                                tracking ||
                                (typeof label === 'string' ? label : undefined),
                            });
                          }}
                        >
                          {label}
                        </Button>
                      ),
                      anchorHandler: (
                        <Button
                          data-cy-id={`desktop-sidebar-nav-${i}`}
                          as="a"
                          styleType="softLink"
                          href={`#${anchor}`}
                          onClick={() => {
                            handleClick && handleClick();
                            trackEvent({
                              modifier:
                                tracking ||
                                (typeof label === 'string' ? label : undefined),
                            });
                          }}
                        >
                          {label}
                        </Button>
                      ),
                    }}
                  </If>
                </ListItem>
              )
            )}
      </List>
    </DesktopSidebarWrapper>
  );
};

DesktopSidebar.defaultProps = defaultProps;

export default track({
  type: 'track',
  action: 'click',
  subject: 'DesktopSidebar',
})(DesktopSidebar);
